import { memo, useState } from "react"
import { useTranslation } from "react-i18next"
import type { DbObject } from "@/db/schema"
import { ObjectType, ObjectStatus } from "@/db/schema"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/app/components/ui/dialog"
import { RadioGroup, RadioGroupItem } from "@/app/components/ui/radio-group"
import { Button } from "@/app/components/ui/button"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ProjectStatusDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  project: DbObject
  unfinished: number
  /** What is being done to the project itself. Default: complete. */
  action?: "complete" | "cancel"
  onConfirm: (childStatus: ObjectStatus) => void
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Confirmation dialog when completing a project/list that still has
 * unfinished children. User chooses what to do with remaining tasks:
 *   - Mark as Completed
 *   - Mark as Canceled
 *
 * Ports v1's ProjectStatusDialog to v2 with Radix Dialog + RadioGroup.
 */
export const ProjectStatusDialog = memo(function ProjectStatusDialog({
  open,
  onOpenChange,
  project,
  unfinished,
  action = "complete",
  onConfirm,
}: ProjectStatusDialogProps) {
  const isCancel = action === "cancel"
  // The rest follows the project by default; the other choice stays available.
  const [childStatus, setChildStatus] = useState<string>(
    String(isCancel ? ObjectStatus.Canceled : ObjectStatus.Completed),
  )
  const isProject = project.type === ObjectType.Project
  const typeLabel = isProject ? "project" : "list"
  const { t } = useTranslation()
  const title =
    project.title?.trim() || (isProject ? t("New Project") : t("New List"))
  const itemWord = unfinished === 1 ? t("item") : t("items")

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>
            {isCancel
              ? t("Cancel this {{type}}, {{title}}?", {
                  type: t(typeLabel),
                  title,
                })
              : t("Complete this {{type}}, {{title}}?", {
                  type: t(typeLabel),
                  title,
                })}
          </DialogTitle>
          <DialogDescription>
            {t(
              "There {{verb}} still {{count}} {{items}} in this {{type}} that you haven't completed. What would you like to do with them?",
              {
                verb: unfinished === 1 ? t("is") : t("are"),
                count: unfinished,
                items: itemWord,
                type: t(typeLabel),
              },
            )}
          </DialogDescription>
        </DialogHeader>

        <RadioGroup
          value={childStatus}
          onValueChange={setChildStatus}
          className="gap-3 py-2"
        >
          <label className="flex cursor-pointer items-center gap-3">
            <RadioGroupItem value={String(ObjectStatus.Completed)} />
            <span className="text-sm">
              {t("Mark remaining to-dos as completed")}
            </span>
          </label>
          <label className="flex cursor-pointer items-center gap-3">
            <RadioGroupItem value={String(ObjectStatus.Canceled)} />
            <span className="text-sm">
              {t("Mark remaining to-dos as canceled")}
            </span>
          </label>
        </RadioGroup>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("Cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={() => onConfirm(Number(childStatus) as ObjectStatus)}
          >
            {isCancel ? t("Confirm") : t("Complete")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
})
